/* Guarda Mundo - Product card carousel + variant thumb preview
 * Spec: specs/0005-product-card.md
 * Preview de variação e hover de mídia: apenas desktop (hover + pointer fino).
 */

(() => {
  const canHover = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
  const VARIANT_ZONE = '[data-gm-variant-zone]';

  const CARD_SRCSET_WIDTHS = [200, 400, 600, 800, 1200];

  const withWidth = (url, w) => {
    if (!url) return url;
    if (/([?&])width=\d+/.test(url)) {
      return url.replace(/([?&])width=\d+/, `$1width=${w}`);
    }
    const sep = url.includes('?') ? '&' : '?';
    return `${url}${sep}width=${w}`;
  };

  const buildSrcset = (url) => {
    if (!url || (!/([?&])width=\d+/.test(url) && !url.includes('cdn.shopify'))) {
      return '';
    }
    return CARD_SRCSET_WIDTHS.map((w) => `${withWidth(url, w)} ${w}w`).join(', ');
  };

  const cacheDefaultSrc = (card) => {
    card.querySelectorAll('.gm-card__img').forEach((img) => {
      if (!img.dataset.defaultSrc) img.dataset.defaultSrc = img.getAttribute('src') || '';
      if (!img.dataset.defaultSrcset) img.dataset.defaultSrcset = img.getAttribute('srcset') || '';
      if (!img.dataset.defaultSizes) img.dataset.defaultSizes = img.getAttribute('sizes') || '';
    });
  };

  const applyImgSource = (img, { src, srcset, sizes }) => {
    if (srcset) {
      img.setAttribute('srcset', srcset);
    } else {
      img.removeAttribute('srcset');
    }
    if (sizes) {
      img.setAttribute('sizes', sizes);
    } else if (!srcset) {
      img.removeAttribute('sizes');
    }
    if (src) img.setAttribute('src', src);
  };

  const hydrateCardGallery = (card) => {
    if (card.dataset.gmGalleryHydrated === '1') return;
    const sizesFallback = card.dataset.gmImgSizes || '';
    card.querySelectorAll('.gm-card__img[data-gm-src]').forEach((img) => {
      const src = img.getAttribute('data-gm-src') || '';
      if (!src) return;
      const srcset = img.getAttribute('data-gm-srcset') || buildSrcset(src);
      const sizes = img.getAttribute('data-gm-sizes') || sizesFallback;
      applyImgSource(img, { src, srcset, sizes });
      img.removeAttribute('data-gm-src');
      img.removeAttribute('data-gm-srcset');
    });
    card.dataset.gmGalleryHydrated = '1';
    cacheDefaultSrc(card);
  };

  const setImage = (card, index) => {
    const total = parseInt(card.dataset.imagesCount || '1', 10);
    if (total <= 1) return;
    if (index !== 0) hydrateCardGallery(card);
    const active = ((index % total) + total) % total;
    card.dataset.activeImage = String(active);
    card.style.setProperty('--gm-card-active', String(active));
    card.querySelectorAll('.gm-card__img').forEach((img) => {
      const idx = parseInt(img.dataset.imageIndex || '0', 10);
      img.classList.toggle('gm-card__img--active', idx === active);
    });
  };

  const advance = (card, delta) => {
    if (card.dataset.variantPreview === '1') return;
    const active = parseInt(card.dataset.activeImage || '0', 10);
    setImage(card, active + delta);
  };

  const setActiveThumb = (card, thumb) => {
    // thumb nulo = volta ao default: a 1a thumb (index "1") reflete a variante
    // atualmente exibida na mídia (fix #5: só um índice ativo por vez).
    const target = thumb || card.querySelector('.gm-card__variant-thumb[data-gm-variant-index="1"]');
    card.querySelectorAll('.gm-card__variant-thumb').forEach((t) => {
      t.classList.toggle('gm-card__variant-thumb--active', t === target);
    });
  };

  const syncDiscountBadge = (card, pctRaw) => {
    const badge = card.querySelector('.gm-card__discount-badge');
    if (!badge) return;

    // Sold-out: badge Esgotado tem prioridade (ADR-050)
    if (card.classList.contains('gm-card--sold-out')) {
      badge.hidden = true;
      badge.textContent = '';
      badge.removeAttribute('aria-label');
      badge.setAttribute('aria-hidden', 'true');
      return;
    }

    const pct = Math.max(0, parseInt(pctRaw, 10) || 0);
    if (pct > 0) {
      badge.hidden = false;
      badge.textContent = `${pct}%`;
      badge.setAttribute('aria-label', `${pct}% de desconto`);
      badge.removeAttribute('aria-hidden');
      return;
    }

    badge.hidden = true;
    badge.textContent = '';
    badge.removeAttribute('aria-label');
    badge.setAttribute('aria-hidden', 'true');
  };

  const restoreDiscountBadge = (card) => {
    const badge = card.querySelector('.gm-card__discount-badge');
    if (!badge) return;
    syncDiscountBadge(card, badge.dataset.gmDiscountDefault || '0');
  };

  const mediaHoverIndex = (card) => {
    if (card.classList.contains('gm-card--sold-out')) return 0;
    const total = parseInt(card.dataset.imagesCount || '1', 10);
    const media = card.querySelector('.gm-card__media');
    if (total > 1 && media?.matches(':hover')) return 1;
    return 0;
  };

  const showVariantPreview = (card, thumb) => {
    const fullSrc = thumb.dataset.gmVariantFull;
    if (!fullSrc) return;

    const img0 = card.querySelector('.gm-card__img[data-image-index="0"]');
    if (!img0) return;

    cacheDefaultSrc(card);
    const sizes = card.dataset.gmImgSizes || '(min-width: 990px) 25vw, (min-width: 750px) 33vw, calc((100vw - 60px) / 2)';
    const srcset = buildSrcset(fullSrc);
    applyImgSource(img0, {
      src: withWidth(fullSrc, 400),
      srcset: srcset || undefined,
      sizes: srcset ? sizes : undefined,
    });
    card.dataset.variantPreview = '1';
    setImage(card, 0);
    setActiveThumb(card, thumb);

    if (thumb.hasAttribute('data-gm-discount-pct')) {
      syncDiscountBadge(card, thumb.getAttribute('data-gm-discount-pct'));
    }
  };

  const clearVariantPreview = (card) => {
    if (card.dataset.variantPreview !== '1') return;

    const img0 = card.querySelector('.gm-card__img[data-image-index="0"]');
    if (img0) {
      applyImgSource(img0, {
        src: img0.dataset.defaultSrc || img0.getAttribute('src'),
        srcset: img0.dataset.defaultSrcset || undefined,
        sizes: img0.dataset.defaultSizes || undefined,
      });
    }

    delete card.dataset.variantPreview;
    setActiveThumb(card, null);
    setImage(card, mediaHoverIndex(card));
    restoreDiscountBadge(card);
  };

  const STRIP_FADE_MS = 200;
  const STRIP_STAGGER_MS = 45;

  const stripLeaveMs = (grid) => {
    const visible = grid.querySelectorAll(
      '.gm-card__variant-thumb--grid:not([hidden]), .gm-card__variant-more:not([hidden])',
    ).length;
    const count = Math.max(visible, 1);
    return STRIP_FADE_MS + STRIP_STAGGER_MS * (count - 1);
  };

  const balanceVariantStrip = (grid) => {
    const card = grid.closest('.gm-card');
    const more = grid.querySelector('[data-gm-variant-more]');
    const thumbs = [...grid.querySelectorAll('.gm-card__variant-thumb--grid')];
    if (!card || !more || !thumbs.length) return;

    const needsMeasure = grid.offsetParent === null;
    if (needsMeasure) card.classList.add('gm-card--variant-strip-measure');

    try {
      thumbs.forEach((thumb, index) => {
        thumb.hidden = false;
        thumb.removeAttribute('aria-hidden');
        thumb.style.setProperty('--gm-variant-anim-order', String(index));
      });
      more.hidden = true;
      more.setAttribute('tabindex', '-1');

      const available = grid.clientWidth;
      if (!available) return;

      more.hidden = false;
      const moreWidth = more.getBoundingClientRect().width;
      more.hidden = true;

      let used = 0;

      for (let i = 0; i < thumbs.length; i++) {
        const thumb = thumbs[i];
        const width = thumb.getBoundingClientRect().width;
        if (width <= 0) return;

        const hasMoreAfter = i < thumbs.length - 1;
        const budget = available - (hasMoreAfter ? moreWidth : 0);

        if (used + width > budget) {
          for (let j = i; j < thumbs.length; j++) {
            thumbs[j].hidden = true;
            thumbs[j].setAttribute('aria-hidden', 'true');
          }
          more.hidden = false;
          more.style.setProperty('--gm-variant-anim-order', String(i));
          more.setAttribute('tabindex', '0');
          return;
        }

        used += width;
        thumb.style.setProperty('--gm-variant-anim-order', String(i));
      }
    } finally {
      card.classList.remove('gm-card--variant-strip-measure');
    }
  };

  const scheduleVariantStripBalance = (grid) => {
    requestAnimationFrame(() => balanceVariantStrip(grid));
  };

  const bindVariantStripBalance = (card) => {
    const grid = card.querySelector('.gm-card__variant-thumbs-grid');
    if (!grid) return;

    const run = () => scheduleVariantStripBalance(grid);

    card.addEventListener('mouseenter', run, { passive: true });

    grid.querySelectorAll('.gm-card__variant-thumb--grid').forEach((img) => {
      if (img.complete) return;
      img.addEventListener('load', run, { once: true });
    });

    if (typeof ResizeObserver !== 'undefined') {
      const observer = new ResizeObserver(run);
      observer.observe(card);
    } else {
      window.addEventListener('resize', run);
    }

    run();
  };

  const bindVariantStripExit = (card) => {
    const grid = card.querySelector('.gm-card__variant-thumbs-grid');
    if (!grid) return;

    let leaveTimer = null;

    const clearLeave = () => {
      if (leaveTimer) {
        clearTimeout(leaveTimer);
        leaveTimer = null;
      }
      card.classList.remove('gm-card--variant-strip-leaving');
    };

    card.addEventListener('mouseenter', clearLeave);

    card.addEventListener('mouseleave', () => {
      if (leaveTimer) {
        clearTimeout(leaveTimer);
        leaveTimer = null;
      }
      card.classList.add('gm-card--variant-strip-leaving');
      leaveTimer = setTimeout(clearLeave, stripLeaveMs(grid));
    });
  };

  // Touch: o track (.gm-card__media-track) vira scroll nativo com scroll-
  // snap (mesmo mecanismo do gm-media-carousel da pagina show) — a foto
  // acompanha o dedo em tempo real. Aqui so sincronizamos a foto ativa /
  // indicator com a posicao do scroll, igual ao carrossel da pagina show.
  //
  // Um unico card fica "aberto" numa foto != main por vez: ao tocar num
  // OUTRO card, o anterior volta pra main (pedido do cliente) — senao o
  // rail ficava cheio de cards presos em fotos 2+.
  //
  // Spec 0056: nas bordas da galeria (ou foto unica via CSS), o swipe na
  // direcao "morta" rola o carrossel de produtos pai em vez de ficar preso.
  let activeSwipedCard = null;
  const HANDOFF_THRESHOLD_PX = 8;
  const EDGE_TOLERANCE_PX = 1;

  const resetMediaToMain = (card) => {
    const track = card.querySelector('[data-gm-card-track]');
    if (track) track.scrollTo({ left: 0, behavior: 'auto' });
    setImage(card, 0);
  };

  const findParentScroller = (el) => {
    let node = el.parentElement;
    while (node && node !== document.body) {
      const style = window.getComputedStyle(node);
      const overflowX = style.overflowX;
      if (
        (overflowX === 'auto' || overflowX === 'scroll') &&
        node.scrollWidth > node.clientWidth + EDGE_TOLERANCE_PX
      ) {
        return node;
      }
      node = node.parentElement;
    }
    return null;
  };

  const bindMediaTrackScroll = (card) => {
    const total = parseInt(card.dataset.imagesCount || '1', 10);
    if (total <= 1) return;

    const track = card.querySelector('[data-gm-card-track]');
    if (!track) return;

    const activeFromScroll = () => {
      const width = track.clientWidth || 1;
      return Math.round(track.scrollLeft / width);
    };

    let startX = 0;
    let startY = 0;
    let handoffX = 0;
    let handoffParentScroll = 0;
    let parentScroller = null;
    let atStart = false;
    let atEnd = false;
    let axis = null; // null | 'x' | 'y'
    let handingOff = false;
    let handedOff = false;

    const beginParentDrag = (parent) => {
      if (!parent || parent.classList.contains('is-dragging')) return;
      parent.classList.add('is-dragging');
      // Fallback pra scrollers sem regra CSS .is-dragging.
      parent.style.scrollSnapType = 'none';
      parent.style.scrollBehavior = 'auto';
    };

    const endParentDrag = (parent) => {
      if (!parent) return;
      parent.classList.remove('is-dragging');
      parent.style.scrollSnapType = '';
      parent.style.scrollBehavior = '';
    };

    track.addEventListener(
      'touchstart',
      (e) => {
        hydrateCardGallery(card);
        if (activeSwipedCard && activeSwipedCard !== card) {
          resetMediaToMain(activeSwipedCard);
        }
        activeSwipedCard = card;

        const touch = e.touches[0];
        if (!touch) return;

        startX = touch.clientX;
        startY = touch.clientY;
        parentScroller = findParentScroller(track);
        const maxScroll = track.scrollWidth - track.clientWidth;
        atStart = track.scrollLeft <= EDGE_TOLERANCE_PX;
        atEnd = track.scrollLeft >= maxScroll - EDGE_TOLERANCE_PX;
        axis = null;
        handingOff = false;
        handedOff = false;
        handoffX = 0;
        handoffParentScroll = 0;
      },
      { passive: true },
    );

    track.addEventListener(
      'touchmove',
      (e) => {
        const touch = e.touches[0];
        if (!touch || !parentScroller) return;

        const dx = touch.clientX - startX;
        const dy = touch.clientY - startY;

        if (!axis) {
          if (Math.abs(dx) < HANDOFF_THRESHOLD_PX && Math.abs(dy) < HANDOFF_THRESHOLD_PX) {
            return;
          }
          axis = Math.abs(dx) >= Math.abs(dy) ? 'x' : 'y';
        }

        if (axis !== 'x') return;

        const towardPrev = dx > 0;
        const towardNext = dx < 0;
        const shouldHandoff =
          (towardPrev && atStart) || (towardNext && atEnd);

        if (!shouldHandoff) return;

        // Ancora no momento do handoff (não no touchstart): evita salto
        // dos ~8px do limiar e acompanha o dedo 1:1 a partir daqui.
        if (!handingOff) {
          handingOff = true;
          handedOff = true;
          handoffX = touch.clientX;
          handoffParentScroll = parentScroller.scrollLeft;
          beginParentDrag(parentScroller);
        }

        e.preventDefault();
        parentScroller.scrollLeft =
          handoffParentScroll - (touch.clientX - handoffX);
      },
      { passive: false },
    );

    const endHandoff = () => {
      if (handedOff) {
        card.dataset.gmSuppressClick = '1';
        window.setTimeout(() => {
          delete card.dataset.gmSuppressClick;
        }, 350);
      }
      endParentDrag(parentScroller);
      handingOff = false;
      handedOff = false;
      axis = null;
      parentScroller = null;
    };

    track.addEventListener('touchend', endHandoff, { passive: true });
    track.addEventListener('touchcancel', endHandoff, { passive: true });

    let ticking = false;
    track.addEventListener(
      'scroll',
      () => {
        if (handingOff) return;
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(() => {
          setImage(card, activeFromScroll());
          ticking = false;
        });
      },
      { passive: true },
    );
  };

  document.addEventListener(
    'click',
    (e) => {
      const target = e.target;
      if (!(target instanceof Element)) return;
      const card = target.closest('.gm-card');
      if (!card || card.dataset.gmSuppressClick !== '1') return;
      delete card.dataset.gmSuppressClick;
      const link = target.closest('a.gm-card__link');
      if (!link || !card.contains(link)) return;
      e.preventDefault();
      e.stopPropagation();
    },
    true,
  );

  document.querySelectorAll('.gm-card').forEach((card) => {
    cacheDefaultSrc(card);
    bindVariantStripBalance(card);
    bindVariantStripExit(card);

    if (!canHover) {
      bindMediaTrackScroll(card);
      return;
    }

    const total = parseInt(card.dataset.imagesCount || '1', 10);
    if (total <= 1) return;

    const media = card.querySelector('.gm-card__media');
    if (!media) return;

    media.addEventListener('mouseenter', () => {
      if (card.classList.contains('gm-card--sold-out')) return;
      if (card.dataset.variantPreview === '1') return;
      hydrateCardGallery(card);
      setImage(card, 1);
    });

    media.addEventListener('mouseleave', () => {
      if (card.classList.contains('gm-card--sold-out')) return;
      if (card.dataset.variantPreview === '1') return;
      setImage(card, 0);
    });
  });

  if (canHover) {
    document.addEventListener(
      'mouseover',
      (e) => {
        const target = e.target;
        if (!(target instanceof Element)) return;

        const zone = target.closest(VARIANT_ZONE);
        if (!zone) return;

        const thumb = target.closest('[data-gm-variant-full]');
        if (!thumb || !zone.contains(thumb)) return;

        const card = zone.closest('.gm-card');
        if (!card) return;

        showVariantPreview(card, thumb);
      },
      true,
    );

    document.addEventListener(
      'mouseout',
      (e) => {
        const target = e.target;
        if (!(target instanceof Element)) return;

        const zone = target.closest(VARIANT_ZONE);
        if (!zone) return;

        const related = e.relatedTarget;
        if (related instanceof Element && zone.contains(related)) return;

        const card = zone.closest('.gm-card');
        if (!card) return;

        clearVariantPreview(card);
      },
      true,
    );
  }

  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Enter' && e.key !== ' ') return;
    const target = e.target;
    if (!(target instanceof Element)) return;

    const more = target.closest('[data-gm-variant-more]');
    if (!more || more.hidden) return;

    const href = more.dataset.href;
    if (!href || href === '#') return;

    e.preventDefault();
    e.stopPropagation();
    window.location.assign(href);
  });

  document.addEventListener('click', (e) => {
    const target = e.target;
    if (!(target instanceof Element)) return;

    const more = target.closest('[data-gm-variant-more]');
    if (more) {
      const href = more.dataset.href;
      if (!href || href === '#') return;
      e.preventDefault();
      e.stopPropagation();
      window.location.assign(href);
      return;
    }

    const prev = target.closest('[data-gm-card-prev]');
    const next = target.closest('[data-gm-card-next]');
    if (!prev && !next) return;

    e.preventDefault();
    e.stopPropagation();

    const card = (prev || next).closest('.gm-card');
    if (!card) return;
    advance(card, prev ? -1 : 1);
  });

  /* Favorito / lista de desejos: gm-wishlist.js (spec 0064). */

  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-mock-add-to-cart]');
    if (!btn) return;
    e.preventDefault();
    btn.textContent = 'MOCKUP - cadastre o produto';
    btn.disabled = true;
    setTimeout(() => {
      btn.textContent = 'Adicionar ao carrinho';
      btn.disabled = false;
    }, 1800);
  });

  /* Tooltip de cor: fixed no body (escapa overflow hidden dos rails/cards).
   * Não esconde em scroll de rail/layout — só ao sair do host (com delay
   * curto pra atravessar gaps entre bolinhas). Scroll só reposiciona. */
  if (canHover) {
    let tip = document.querySelector('[data-gm-swatch-tip]');
    if (!tip) {
      tip = document.createElement('div');
      tip.className = 'gm-swatch-tip';
      tip.setAttribute('data-gm-swatch-tip', '');
      tip.setAttribute('aria-hidden', 'true');
      document.body.appendChild(tip);
    }

    let tipHost = null;
    let hideTimer = null;
    const HIDE_DELAY_MS = 100;

    const cancelHide = () => {
      if (hideTimer) {
        clearTimeout(hideTimer);
        hideTimer = null;
      }
    };

    const hideTip = () => {
      cancelHide();
      tipHost = null;
      tip.classList.remove('is-visible');
      tip.textContent = '';
    };

    const scheduleHide = () => {
      cancelHide();
      hideTimer = setTimeout(hideTip, HIDE_DELAY_MS);
    };

    const positionTip = (host) => {
      if (tipHost !== host) return;
      const rect = host.getBoundingClientRect();
      if (rect.width <= 0 || rect.height <= 0) {
        hideTip();
        return;
      }
      const tipRect = tip.getBoundingClientRect();
      let left = rect.left + rect.width / 2 - tipRect.width / 2;
      let top = rect.bottom + 6;
      const maxLeft = window.innerWidth - tipRect.width - 8;
      if (left > maxLeft) left = Math.max(8, maxLeft);
      if (left < 8) left = 8;
      if (top + tipRect.height > window.innerHeight - 8) {
        top = Math.max(8, rect.top - tipRect.height - 6);
      }
      tip.style.transform = `translate(${Math.round(left)}px, ${Math.round(top)}px)`;
    };

    const placeTip = (host) => {
      const label = (host.getAttribute('data-gm-cor') || '').trim();
      if (!label) {
        hideTip();
        return;
      }
      cancelHide();
      tipHost = host;
      tip.textContent = label;
      tip.classList.add('is-visible');
      positionTip(host);
      requestAnimationFrame(() => positionTip(host));
    };

    const cardHostFromEvent = (e) => {
      const target = e.target;
      if (!(target instanceof Element)) return null;
      const host = target.closest('[data-gm-cor]');
      if (!host || !host.closest('.gm-card')) return null;
      return host;
    };

    document.addEventListener(
      'pointerover',
      (e) => {
        const host = cardHostFromEvent(e);
        if (!host) return;
        if (host !== tipHost) placeTip(host);
        else cancelHide();
      },
      { passive: true },
    );

    document.addEventListener(
      'pointerout',
      (e) => {
        if (!tipHost) return;
        const related = e.relatedTarget;
        if (related instanceof Node && tipHost.contains(related)) return;

        const leavingHost =
          e.target === tipHost ||
          (e.target instanceof Node && tipHost.contains(e.target));
        if (!leavingHost) return;

        const next =
          related instanceof Element ? related.closest('[data-gm-cor]') : null;
        if (next && next.closest('.gm-card')) {
          placeTip(next);
          return;
        }
        scheduleHide();
      },
      { passive: true },
    );

    document.addEventListener(
      'scroll',
      () => {
        if (!tipHost) return;
        positionTip(tipHost);
      },
      { passive: true, capture: true },
    );
  }

  // ADR-050: esgotados por último em grades/rails (sem depender do sort de coleção).
  // appendChild em track com scroll-snap pode ancorar scrollLeft no fim — resetar.
  const listingContainers = () =>
    [
      ...document.querySelectorAll('.gm-collection-category__grid'),
      ...document.querySelectorAll(
        '.gm-collection-modelo__grid [data-gm-rail-track]',
      ),
      ...document.querySelectorAll('.gm-product-rail [data-gm-rail-track]'),
      ...document.querySelectorAll('.gm-product-list__grid'),
    ].filter((grid) => grid.querySelector(':scope > .gm-card'));

  const resetRailScroll = (track) => {
    const viewport =
      track.closest('[data-gm-rail-viewport]') ||
      track.closest('.gm-product-rail__viewport') ||
      track.parentElement;
    if (!viewport || viewport === track) return;
    const prevBehavior = viewport.style.scrollBehavior;
    viewport.style.scrollBehavior = 'auto';
    viewport.scrollLeft = 0;
    viewport.style.scrollBehavior = prevBehavior;
    viewport.dispatchEvent(new Event('scroll', { bubbles: false }));
  };

  const reorderSoldOutLast = (grid) => {
    const cards = [...grid.querySelectorAll(':scope > .gm-card')];
    if (cards.length < 2) return;
    const available = [];
    const soldOut = [];
    for (const card of cards) {
      if (card.getAttribute('data-gm-available') === '0') soldOut.push(card);
      else available.push(card);
    }
    if (!soldOut.length) return;
    const next = [...available, ...soldOut];
    const unchanged = next.every((card, i) => card === cards[i]);
    if (unchanged) return;
    for (const card of next) grid.appendChild(card);
    if (grid.matches('[data-gm-rail-track]')) resetRailScroll(grid);
  };

  listingContainers().forEach(reorderSoldOutLast);
})();
